import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    message,
    Modal,
    Popover,
    Row,
    Select,
    Spin,
    Typography
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import {
    CheckCircleTwoTone,
    CheckOutlined,
    CloseCircleTwoTone,
    EditOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import RunningPnLLineChart from '../../components/visualizations/running-pnl-chart';
import { useManagementStore, useUserState } from '../../store';
import axios from '../../utilities/axios';
import { ADMIN, FORMAT } from '../../utilities/functions';

import './account.css';

const { Text } = Typography;
const NY_TZ = 'America/New_York';

const toNyEndOfDayISOString = (value) => {
    if (!value) return null;
    return dayjs.tz(value.format('YYYY-MM-DD'), 'YYYY-MM-DD', NY_TZ).endOf('day').toISOString();
};

const toNyCalendarDate = (value) => (value ? dayjs(value).tz(NY_TZ) : null);

const RISK_CONTROLS = [
    { label: 'Max Loss ($)', value: 'max_loss', apiKey: 'maxLoss', hasInput: true },
    { label: 'Total Loss ($)', value: 'total_loss', apiKey: 'totalLoss', hasInput: true },
    { label: 'Equity Limit ($)', value: 'equity_limit', apiKey: 'equityLimit', hasInput: true },
    {
        label: 'Unwind All positions (EDT)',
        value: 'unwind_all',
        apiKey: 'unwindAllPositions',
        hasInput: true
    },
    {
        label: 'Prevent Premarket Trading',
        value: 'prevent_premarket',
        apiKey: 'preventPremarketTrading'
    },
    {
        label: 'Prevent After Market Trading',
        value: 'prevent_aftermarket',
        apiKey: 'preventAfterMarketTrading'
    }
];

const WORKFLOW_STATUS_OPTIONS = [
    { label: 'Needs Review', value: 'needs_review' },
    { label: 'Needs Review - Awaiting Info', value: 'needs_review_awaiting_info' },
    { label: 'Reviewed - Contact', value: 'reviewed_contact' },
    { label: 'Contacted', value: 'reviewed_contacted' },
    { label: 'Responded - Awaiting Payment', value: 'awaiting_payment' },
    { label: 'Reviewed - Not Eligible', value: 'not_eligible' },
    { label: 'Paid', value: 'paid' }
];

// Remove CLOSED_ACCOUNT_STATUS_OPTIONS, not needed

const resetUserPassword = async (token, userId, newPassword) =>
    axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/user/adminResetPassword`,
        { userId, newPassword },
        { headers: { token } }
    );

const resetAccountEquity = async (token, payload) =>
    axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/admin-reset-equity`,
        payload,
        { headers: { token } }
    );

// --- Activate/Deactivate Account API ---
const activateFundedAccount = async (token, payload) => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/activate-funded-account`,
        payload,
        { headers: { token } }
    );
    return data;
};

const deactivateFundedAccount = async (token, accountId) => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/deactivate-funded-account`,
        { accountId },
        { headers: { token } }
    );
    return data;
};

const archiveFundedAccount = async (token, accountId) => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/archive-funded-account/${accountId}`,
        {},
        { headers: { token } }
    );
    return data;
};

const updateEndDate = async (token, accountId, expirationDate) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-end-date/${accountId}`,
        { expirationDate },
        { headers: { token } }
    );
    return data;
};

const updateDrawdownCheckStatus = async (token, accountId, drawdownCheckStatus) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-drawdown-check-status/${accountId}`,
        { drawdownCheckStatus },
        { headers: { token } }
    );
    return data;
};

const fetchEffectiveRiskConfig = async (token, accountId) => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/effective-risk-config/${accountId}`,
        { headers: { token } }
    );
    return {
        effectiveRiskConfig: data.effectiveRiskConfig || {},
        riskConfigOverrides: data.riskConfigOverrides || {}
    };
};

const patchRiskOverrides = async (token, accountId, overrides) => {
    const { data } = await axios.patch(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/risk-override/${accountId}`,
        { overrides },
        { headers: { token } }
    );
    return data;
};

// --- Mark/clear risk violation (affects payout eligibility) ---
const updateRiskViolation = async ({ token, accountId, value, reason }) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/mark-risk-violation/${accountId}`,
        { value, reasons: reason },
        { headers: { token } }
    );
    return data;
};

// --- Update account workflow status ---
const updateAccountStatus = async (token, accountId, workflowStatus) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-workflow-status/${accountId}`,
        { workflowStatus },
        { headers: { token } }
    );
    return data;
};

// --- Save note ---
const saveNote = async (token, accountId, note) => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/save_note/${accountId}`,
        { note },
        { headers: { token } }
    );
    return data;
};

// --- Trade Execution Report Download Handler ---
const downloadTradeExecutionReport = async ({ accountId, userId }) => {
    try {
        // The API expects an authenticated request with bearer token
        const config = {
            // headers: {
            //   Authorization: `Bearer ${userToken}`
            // },
            responseType: 'blob'
        };
        const params = new URLSearchParams({ accountUniqueId: accountId });
        if (userId) {
            params.set('userId', userId);
        }
        const url = `/trades/trade-report-per-account-csv?${params.toString()}`;
        const response = await axios.get(url, config);

        // Handle filename from headers if provided
        const now = new Date();
        const dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
            2,
            '0'
        )}-${String(now.getDate()).padStart(2, '0')}`;
        let filename = `${accountId}-trade-execution-report-${dateString}.csv`;
        const cd =
            response.headers &&
            (response.headers['content-disposition'] || response.headers['Content-Disposition']);
        if (cd && cd.includes('filename=')) {
            filename = cd.split('filename=')[1].replace(/"/g, '');
        }

        // Create and download the blob
        const blob = new Blob([response.data], { type: 'text/csv' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
            const urlBlob = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = urlBlob;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(urlBlob);
            }, 200);
        }
        message.success('Trade Execution Report downloaded.');
    } catch (err) {
        message.error('Failed to download Trade Execution Report');
    }
};

export default function AdminAccountPage() {
    const { getUser } = useUserState();
    const user = getUser();
    const { accountId } = useParams();
    const navigate = useNavigate();
    const { setViewAs, setCompany, setLastFundedAccountId } = useManagementStore((state) => state);

    const [password, setPassword] = useState('');
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [initialFunds, setInitialFunds] = useState('12500');
    const [leverage, setLeverage] = useState('4');
    const popoverRef = useRef();
    const [activatePopoverVisible, setActivatePopoverVisible] = useState(false);
    const [deactivatePopoverVisible, setDeactivatePopoverVisible] = useState(false);
    const [expirationDays, setExpirationDays] = useState(90);
    const [activateLoading, setActivateLoading] = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const passwordInputRef = useRef();
    const [resetEquityLoading, setResetEquityLoading] = useState(false);
    const [pttResetOnReinstate, setPttResetOnReinstate] = useState(false);
    const [endDateValue, setEndDateValue] = useState(null);
    const [endDateSaving, setEndDateSaving] = useState(false);

    // New state for trade report download loading
    const [tradeReportLoading, setTradeReportLoading] = useState(false);

    // Reset equity options state
    const [removeExistingTrades, setRemoveExistingTrades] = useState(true);
    const [resetExpirationDate, setResetExpirationDate] = useState(true);
    const [resetExpirationDays, setResetExpirationDays] = useState(90);

    // Risk controls save loading state
    const [riskSaveLoading, setRiskSaveLoading] = useState(false);

    // Note state
    const [note, setNote] = useState('');
    const [noteSaveLoading, setNoteSaveLoading] = useState(false);

    // Payout eligibility modal state
    const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
    const [eligibilityModalData, setEligibilityModalData] = useState({
        nextValue: null,
        existingReasons: null
    });
    const [selectedReasons, setSelectedReasons] = useState([]);

    // Remove closedStatus state and loading, not needed

    const handleActivatePopoverOpen = (open) => {
        setActivatePopoverVisible(open);
        if (!open) setPttResetOnReinstate(false);
        if (open) setPasswordFocused(false);
    };
    const handleDeactivatePopoverOpen = (open) => {
        setDeactivatePopoverVisible(open);
        if (open) setPasswordFocused(false);
    };
    const handleEquityPopoverOpen = (open) => {
        setPopoverVisible(open);
        if (open) setPasswordFocused(false);
    };

    const { data: fundedAccountsData } = useQuery(['funded-accounts'], () =>
        ADMIN.fetchAccounts(user?.token)
    );
    const {
        data: account,
        isLoading,
        refetch
    } = useQuery(['funded-account', accountId], () => ADMIN.fetchAccount(user?.token, accountId), {
        enabled: !!accountId
    });

    const isPCT = account?.type === 'pct';
    const isPTT = account?.type === 'ptt';
    const isPCTorPTT = isPCT || isPTT;
    const hasAssignedUser = Boolean(account?.user?.id);
    const unassignedJournalHref =
        !hasAssignedUser && account?.accountUniqueId ? `/admin/u/${account.accountUniqueId}` : null;
    const showPasswordReset = !isPCTorPTT && hasAssignedUser;
    const showGenericEquityReset = account?.type === 'simulator_ttf';
    const showRiskControls = !isPCTorPTT;
    const canArchiveAccount = isPCTorPTT && !account?.isActive && !account?.archived;

    useEffect(() => {
        const normalizedAccountId = account?.accountUniqueId || accountId || null;
        if (!normalizedAccountId) return;

        setLastFundedAccountId(normalizedAccountId);
    }, [account?.accountUniqueId, accountId, setLastFundedAccountId]);

    // Fetch effective risk config and overrides from Phantom
    const {
        data: riskConfigData,
        isLoading: isRiskConfigLoading,
        refetch: refetchRiskConfig
    } = useQuery(
        ['effective-risk-config', account?.id],
        () => (account?.id ? fetchEffectiveRiskConfig(user?.token, account.id) : undefined),
        { enabled: !!account?.id && !['pct', 'ptt'].includes(account?.type) }
    );
    const effectiveRiskConfig = riskConfigData?.effectiveRiskConfig || {};
    const riskConfigOverrides = riskConfigData?.riskConfigOverrides || {};

    // Mutation for payout eligibility (risk violation)
    const { mutate: mutateRiskViolation, isLoading: isUpdatingRisk } = useMutation({
        mutationFn: (params) => updateRiskViolation(params),
        onSuccess: (data) => {
            message.success(data?.message || 'Updated payout eligibility');
            if (refetch) refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update payout eligibility');
        }
    });

    // Mutation for updating account status
    const { mutate: mutateAccountStatus, isLoading: isUpdatingStatus } = useMutation({
        mutationFn: (params) =>
            updateAccountStatus(params.token, params.accountId, params.workflowStatus),
        onSuccess: () => {
            message.success('Account status updated successfully');
            if (refetch) refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update account status');
        }
    });

    const { mutate: mutateDrawdownCheckStatus, isLoading: isUpdatingDrawdownStatus } = useMutation({
        mutationFn: (params) =>
            updateDrawdownCheckStatus(params.token, params.accountId, params.drawdownCheckStatus),
        onSuccess: (data) => {
            message.success(data?.message || 'OUT marker updated');
            if (refetch) refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update OUT marker');
        }
    });

    const confirmChangePayoutEligibility = (nextValue, existingReasons = null) => {
        // If existingReasons not provided, try to get them from the account
        let reasonsToLoad = existingReasons;
        if (!reasonsToLoad && account) {
            reasonsToLoad = account?.riskViolationReason || null;
        }

        const initialReasons =
            reasonsToLoad && Array.isArray(reasonsToLoad) ? [...reasonsToLoad] : [];
        setEligibilityModalData({ nextValue, existingReasons: reasonsToLoad });
        setSelectedReasons(initialReasons);
        setEligibilityModalOpen(true);
    };

    const handleEligibilityModalOk = () => {
        const { nextValue } = eligibilityModalData;
        const eligible = nextValue === false; // false => eligible

        if (!eligible && (!selectedReasons || selectedReasons.length === 0)) {
            message.error('Please select at least one reason');
            return;
        }

        mutateRiskViolation({
            token: user?.token,
            accountId: account.id,
            value: nextValue,
            reason: selectedReasons && selectedReasons.length > 0 ? selectedReasons : undefined
        });

        setEligibilityModalOpen(false);
    };

    const handleEligibilityModalCancel = () => {
        setEligibilityModalOpen(false);
        setSelectedReasons([]);
    };

    const REASON_OPTIONS = [
        { value: 'equityBelow9500', label: 'Equity Below $9,500' },
        { value: 'equityBelow10500', label: 'Equity Below $10,500' },
        { value: 'maxResets3', label: 'Maximum Resets (3)' },
        { value: 'didNotMeet36Days', label: 'Minimum Trading Days (36)' },
        { value: 'didNotMeet10Day2TradesRollingAvg', label: '10-Day Rolling Avg of 2 Trades' },
        { value: 'stocksBelow5', label: 'Traded Stocks Below $5' },
        { value: 'tradeOver30PctTotalPnL', label: 'Trade > 30% Total PnL' },
        { value: 'leveragedETF', label: 'Leveraged ETF' },
        { value: 'other', label: 'Other' }
    ];

    const eligible = eligibilityModalData.nextValue === false; // false => eligible
    let modalTitle;
    if (eligible) {
        modalTitle = 'Mark Eligible for Payout?';
    } else if (eligibilityModalData.existingReasons) {
        modalTitle = 'Edit Risk Violation Reasons';
    } else {
        modalTitle = 'Mark NOT Eligible (Risk Violation)?';
    }

    // No closedStatus state, so no useEffect for it

    const accountOptions = useMemo(() => {
        if (!fundedAccountsData) return [];
        return fundedAccountsData
            .filter((a) => ['simulator_ttf', 'pct', 'ptt'].includes(a.type))
            .map((acc) => ({
                label: `${acc.accountUniqueId} - ${acc.user?.email || 'Unassigned'}`,
                value: acc.accountUniqueId,
                email: acc.user?.email || 'Unassigned'
            }));
    }, [fundedAccountsData]);

    const passwordRules = [
        { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
        { label: 'At least one lowercase letter', test: (pw) => /[a-z]/.test(pw) },
        { label: 'At least one uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
        { label: 'At least one digit or symbol', test: (pw) => /[\d\W_]/.test(pw) }
    ];
    const isPasswordValid = password && passwordRules.every((rule) => rule.test(password));

    // Calculate buying power
    const buyingPower = Number(initialFunds) * Number(leverage) || 0;

    const handleJournalClick = async () => {
        if (!account?.accountUniqueId) {
            message.error('Account not loaded');
            return;
        }

        let company = 'Personal';
        if (account.type === 'pct') company = 'Peak Capital Trading';
        else if (account.type === 'ptt') company = 'Bear Bull Traders';
        else if (account.type === 'simulator_ttd' || account.type === 'simulator_ttf')
            company = 'Trading Terminal';
        setCompany(company);

        if (!account?.user?.id) return;

        try {
            const response = await axios.get(`/crud/users/${account.user?.id}/token`, {
                headers: { token: user?.token }
            });
            const userToken = response.data.token;

            setViewAs(
                { token: userToken, id: account.user?.id },
                { token: user?.token, id: user?.id }
            );
            navigate('/');
        } catch (error) {
            message.error('Failed to switch to user journal');
        }
    };

    // --- Risk controls state and effect logic ---
    const [selectedRiskControls, setSelectedRiskControls] = useState([]);
    const [riskControlValues, setRiskControlValues] = useState({
        max_loss: '',
        total_loss: '',
        equity_limit: '',
        unwind_all: ''
    });
    const [riskDropdownOpen, setRiskDropdownOpen] = useState(false);

    // Track last loaded config to avoid unnecessary resets
    const lastConfigRef = useRef(null);

    // --- [FIXED] Map effectiveRiskConfig to UI state for editing ---
    useEffect(() => {
        if (
            effectiveRiskConfig &&
            JSON.stringify(effectiveRiskConfig) !== JSON.stringify(lastConfigRef.current)
        ) {
            const selected = [];
            const values = { max_loss: '', total_loss: '', equity_limit: '', unwind_all: '' };

            RISK_CONTROLS.forEach(({ value, apiKey, hasInput }) => {
                const config = effectiveRiskConfig[apiKey];
                const isEffectivelyEnabled =
                    typeof config === 'object' ? config.enabled : config === true;

                if (isEffectivelyEnabled) {
                    selected.push(value);
                    if (hasInput && typeof config === 'object') {
                        // Fix: For unwind_all, use .time, for others use .value
                        if (value === 'unwind_all' && config.time) {
                            values[value] = config.time;
                        } else if (value === 'unwind_all' && config.value) {
                            // fallback for legacy
                            values[value] = config.value;
                        } else if (config.value !== undefined && config.value !== null) {
                            values[value] = String(config.value);
                        }
                    }
                }
            });

            setSelectedRiskControls(selected);
            setRiskControlValues(values);
            lastConfigRef.current = effectiveRiskConfig;
        }
    }, [effectiveRiskConfig]);

    // Load note from account when account data is available
    useEffect(() => {
        if (account?.notes !== undefined && account?.notes !== null) {
            setNote(account.notes || '');
        }
    }, [account?.notes]);

    useEffect(() => {
        setEndDateValue(toNyCalendarDate(account?.accountEndDate));
        setPttResetOnReinstate(false);
    }, [account?.accountEndDate, account?.id]);

    const handleRiskChange = (values) => setSelectedRiskControls(values);

    const handleRiskInputChange = (key, value) => {
        setRiskControlValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleRiskDateChange = (key, date) => {
        setRiskControlValues((prev) => ({
            ...prev,
            [key]: date ? date.toISOString() : ''
        }));
    };

    const handleRiskReset = () => {
        setSelectedRiskControls([]);
        setRiskControlValues({ max_loss: '', total_loss: '', equity_limit: '', unwind_all: '' });
    };

    // --- PATCH risk overrides on Save, require value for enabled risks ---
    const handleRiskSave = async () => {
        if (!account?.id) return;
        setRiskSaveLoading(true);

        // Validate: if any enabled risk with input is missing value, error and abort
        const missingInput = RISK_CONTROLS.find(({ value: key, hasInput }) => {
            if (hasInput && selectedRiskControls.includes(key)) {
                const val = riskControlValues[key];
                if (key === 'unwind_all') {
                    return !val;
                }
                return val === '' || val === null || val === undefined || Number.isNaN(Number(val));
            }
            return false;
        });

        if (missingInput) {
            message.error(`Please enter a value for "${missingInput.label}" before enabling.`);
            setRiskSaveLoading(false);
            return;
        }

        // Start with a true copy of the original overrides. We will modify and send this.
        const newOverrides = { ...(riskConfigOverrides || {}) };

        // To know when to REMOVE an override (i.e., revert to default), we need to know the default state.
        // We can deduce the default ("base") config by subtracting the current overrides from the effective config.
        const baseConfig = { ...effectiveRiskConfig };
        if (riskConfigOverrides) {
            Object.keys(riskConfigOverrides).forEach((key) => {
                delete baseConfig[key];
            });
        }

        RISK_CONTROLS.forEach(({ value: key, apiKey, hasInput }) => {
            // 1. Get the final desired state from the UI
            const isUiEnabled = selectedRiskControls.includes(key);
            let uiValue = null;
            if (hasInput) {
                if (key === 'unwind_all') {
                    uiValue = riskControlValues[key] || null;
                } else if (riskControlValues[key] !== '') {
                    uiValue = Number(riskControlValues[key]);
                } else {
                    uiValue = null;
                }
            }

            // 2. Get the current effective state (what the user sees is active)
            const effectiveConfig = effectiveRiskConfig[apiKey];
            const isEffectivelyEnabled =
                typeof effectiveConfig === 'object'
                    ? effectiveConfig?.enabled
                    : effectiveConfig === true;
            // Fix: For unwind_all, compare .time, for others .value
            let effectiveValue = null;
            if (hasInput && typeof effectiveConfig === 'object') {
                if (key === 'unwind_all') {
                    effectiveValue = effectiveConfig?.time || effectiveConfig?.value || null;
                } else {
                    effectiveValue = effectiveConfig?.value;
                }
            }

            // 3. Decide if the user's action resulted in a change from the current state.
            const stateChanged = isUiEnabled !== isEffectivelyEnabled;
            const valueChanged = hasInput && isUiEnabled && uiValue !== effectiveValue;

            if (stateChanged || valueChanged) {
                // A change was made. Now, should we add/update an override, or remove it?
                const baseState = baseConfig[apiKey];
                const isBaseEnabled =
                    typeof baseState === 'object' ? baseState?.enabled : baseState === true;
                let baseValue = null;
                if (hasInput && typeof baseState === 'object') {
                    if (key === 'unwind_all') {
                        baseValue = baseState?.time || baseState?.value || null;
                    } else {
                        baseValue = baseState?.value;
                    }
                }

                // If the user's desired state is the same as the default, we can safely REMOVE the override.
                if (isUiEnabled === isBaseEnabled && uiValue === baseValue) {
                    delete newOverrides[apiKey];
                } else {
                    // The user's choice differs from the default, so an override is required.
                    const overridePayload = { enabled: isUiEnabled };
                    // Only include the 'value' in the payload if the rule is being ENABLED.
                    if (hasInput && isUiEnabled) {
                        if (key === 'unwind_all') {
                            overridePayload.time = uiValue;
                        } else {
                            overridePayload.value = uiValue;
                        }
                    }
                    newOverrides[apiKey] = overridePayload;
                }
            }
        });

        // 4. Check if the final calculated overrides are actually different from the original ones.
        if (JSON.stringify(newOverrides) === JSON.stringify(riskConfigOverrides)) {
            message.info('No changes to risk controls');
            setRiskDropdownOpen(false);
            setRiskSaveLoading(false);
            return;
        }

        // 5. Send the final, minimal, merged set of overrides to the backend.
        try {
            await patchRiskOverrides(user?.token, account.id, newOverrides);
            message.success('Risk controls saved successfully');
            setRiskDropdownOpen(false);
            refetchRiskConfig(); // Refetch to get the new effective config
        } catch (err) {
            message.error('Failed to save risk controls');
        } finally {
            setRiskSaveLoading(false);
        }
    };

    // --- Handle status change ---
    const handleStatusChange = (newStatus) => {
        if (!account?.id) return;
        mutateAccountStatus({
            token: user?.token,
            accountId: account.id,
            workflowStatus: newStatus
        });
    };

    // --- Trade Execution Report Download Handler ---
    const handleReportDownload = async () => {
        if (!account?.accountUniqueId) {
            message.error('Account not loaded');
            return;
        }
        setTradeReportLoading(true);
        try {
            await downloadTradeExecutionReport({
                accountId: account.accountUniqueId,
                userId: account.user?.id
            });
        } finally {
            setTradeReportLoading(false);
        }
    };

    // --- Save note handler ---
    const handleSaveNote = async () => {
        if (!accountId) return;
        if (!note.trim()) {
            message.error('Note cannot be empty');
            return;
        }
        setNoteSaveLoading(true);
        try {
            await saveNote(user?.token, accountId, note.trim());
            message.success('Note saved successfully');
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to save note');
        } finally {
            setNoteSaveLoading(false);
        }
    };

    const handleSaveEndDate = async () => {
        if (!account?.id) return;
        setEndDateSaving(true);
        try {
            await updateEndDate(user?.token, account.id, toNyEndOfDayISOString(endDateValue));
            message.success('End date updated successfully');
            if (refetch) refetch();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to update end date');
        } finally {
            setEndDateSaving(false);
        }
    };

    const handleArchiveAccount = async () => {
        if (!account?.id) return;
        setArchiveLoading(true);
        try {
            await archiveFundedAccount(user?.token, account.id);
            message.success('Account archived successfully');
            if (refetch) refetch();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to archive account');
        } finally {
            setArchiveLoading(false);
        }
    };

    const handlePttReset = async () => {
        if (!account?.id) return;
        setActivateLoading(true);
        try {
            await activateFundedAccount(user?.token, {
                accountId: account.id,
                reinstateMode: 'reset'
            });
            message.success('TRPTT account reset and reactivated');
            if (refetch) refetch();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to reset account');
        } finally {
            setActivateLoading(false);
        }
    };

    const handleMarkIn = () => {
        if (!account?.id) return;

        mutateDrawdownCheckStatus({
            token: user?.token,
            accountId: account.id,
            drawdownCheckStatus: 'in'
        });
    };

    // --- Show loading spinner only if account or risk config is loading ---
    if (isLoading || !account || (showRiskControls && isRiskConfigLoading)) {
        return (
            <div
                style={{
                    width: '100%',
                    height: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Spin size="large" tip="Loading account..." />
            </div>
        );
    }

    return (
        <div className="admin-account-page" style={{ width: '100%', padding: 24 }}>
            <Card className="admin-account-hero" bordered={false}>
                <Row align="top" justify="space-between" gutter={[16, 16]}>
                    <Col flex="auto">
                        <div className="admin-account-hero-main">
                            <div className="admin-account-hero-nav">
                                <Select
                                    className="admin-account-selector"
                                    size="large"
                                    showSearch
                                    style={{
                                        width: 500,
                                        fontWeight: 700,
                                        background: '#f5f5f5',
                                        borderRadius: 6,
                                        padding: 2
                                    }}
                                    dropdownStyle={{ minWidth: 340 }}
                                    placeholder="Select account"
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={accountOptions}
                                    value={account.accountUniqueId}
                                    onChange={(val) => navigate(`/admin/${val}`)}
                                    autoComplete="off"
                                    optionLabelProp="label"
                                    optionRender={(option) => (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                fontWeight:
                                                    option.value === account.accountUniqueId
                                                        ? 700
                                                        : 400,
                                                color:
                                                    option.value === account.accountUniqueId
                                                        ? '#1890ff'
                                                        : undefined
                                            }}
                                        >
                                            <span>
                                                {option.label}
                                                {option.value === account.accountUniqueId && (
                                                    <span
                                                        style={{
                                                            marginLeft: 8,
                                                            color: '#52c41a',
                                                            fontWeight: 600,
                                                            fontSize: 12
                                                        }}
                                                    >
                                                        Selected
                                                    </span>
                                                )}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: '#888'
                                                }}
                                            >
                                                {option.data.email}
                                            </span>
                                        </div>
                                    )}
                                    renderValue={() => (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontWeight: 700 }}>
                                                {account.user?.email || 'Unassigned account'}
                                            </span>
                                            <span style={{ fontSize: 12, color: '#888' }}>
                                                {account.accountUniqueId}
                                            </span>
                                        </div>
                                    )}
                                />
                                <Button
                                    className="admin-account-journal-link"
                                    style={{
                                        fontWeight: 500,
                                        color: '#1890ff',
                                        textDecoration: 'underline',
                                        fontSize: 14,
                                        marginLeft: 0
                                    }}
                                    href={unassignedJournalHref || undefined}
                                    onClick={unassignedJournalHref ? undefined : handleJournalClick}
                                >
                                    View Account Journal
                                </Button>
                            </div>
                            <div className="admin-account-chip-row">
                                <span className="admin-account-chip admin-account-chip--type">
                                    {account.type?.toUpperCase() || 'ACCOUNT'}
                                </span>
                                {account.drawdownCheckStatus === 'out' && (
                                    <span className="admin-account-chip admin-account-chip--out">
                                        OUT
                                    </span>
                                )}
                                {account.riskViolation === true && (
                                    <span className="admin-account-chip admin-account-chip--risk">
                                        Risk Violation
                                    </span>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col flex="430px">
                        <div className="admin-account-top-actions">
                            {!account.isActive && account.workflowStatus && (
                                <Select
                                    size="small"
                                    value={account.workflowStatus || null}
                                    style={{ width: 200 }}
                                    onChange={(val) => handleStatusChange(val)}
                                    loading={isUpdatingStatus}
                                    placeholder="Select status"
                                    options={WORKFLOW_STATUS_OPTIONS}
                                />
                            )}
                            <Select
                                size="small"
                                value={account?.riskViolation === true}
                                style={{ width: 160 }}
                                onChange={(val) => {
                                    if (val === (account?.riskViolation === true)) return;
                                    confirmChangePayoutEligibility(val);
                                }}
                                loading={isUpdatingRisk}
                                options={[
                                    {
                                        label: (
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}
                                            >
                                                <CheckCircleTwoTone twoToneColor="#389e0d" />
                                                Eligible
                                            </span>
                                        ),
                                        value: false
                                    },
                                    {
                                        label: (
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}
                                            >
                                                <CloseCircleTwoTone twoToneColor="#d4380d" />
                                                Not Eligible
                                            </span>
                                        ),
                                        value: true
                                    }
                                ]}
                            />
                            {account?.riskViolation === true && (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        const existingReasons = account?.riskViolationReason || [];
                                        confirmChangePayoutEligibility(
                                            true,
                                            Array.isArray(existingReasons) ? existingReasons : []
                                        );
                                    }}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    Edit Reasons
                                </Button>
                            )}
                            {canArchiveAccount && (
                                <Button
                                    size="small"
                                    danger
                                    loading={archiveLoading}
                                    onClick={() => {
                                        Modal.confirm({
                                            title: 'Archive Account?',
                                            content:
                                                'This will move the account to Archived and remove it from the active review workflow.',
                                            onOk: handleArchiveAccount
                                        });
                                    }}
                                >
                                    Archive
                                </Button>
                            )}
                            {isPCTorPTT && account.drawdownCheckStatus === 'out' && (
                                <Button
                                    size="small"
                                    loading={isUpdatingDrawdownStatus}
                                    onClick={() => {
                                        Modal.confirm({
                                            title: 'Mark IN?',
                                            content:
                                                'This will remove the OUT marker and allow future automated drawdown checks to trigger again on later uploads.',
                                            onOk: handleMarkIn
                                        });
                                    }}
                                >
                                    Mark IN
                                </Button>
                            )}
                            {account.isActive ? (
                                <>
                                    <div className="admin-account-status-pill admin-account-status-pill--active">
                                        <CheckCircleTwoTone
                                            twoToneColor="#52c41a"
                                            style={{ fontSize: 18 }}
                                        />
                                        <span>Active</span>
                                    </div>
                                    <Popover
                                        content={
                                            <div style={{ minWidth: 240 }}>
                                                <div
                                                    style={{
                                                        marginBottom: 8,
                                                        color: '#faad14',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Warning: Deactivation will happen immediately.
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        gap: 8
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setDeactivatePopoverVisible(false)
                                                        }
                                                        disabled={deactivateLoading}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        danger
                                                        loading={deactivateLoading}
                                                        onClick={async () => {
                                                            if (!account?.id) return;
                                                            setDeactivateLoading(true);
                                                            try {
                                                                await deactivateFundedAccount(
                                                                    user?.token,
                                                                    account.id
                                                                );
                                                                message.success(
                                                                    isPCTorPTT
                                                                        ? 'Account moved to Needs Review'
                                                                        : 'Account deactivated immediately'
                                                                );
                                                                setDeactivatePopoverVisible(false);
                                                                if (refetch) refetch();
                                                            } catch (err) {
                                                                message.error(
                                                                    err?.response?.data?.message ||
                                                                        'Failed to deactivate account'
                                                                );
                                                            } finally {
                                                                setDeactivateLoading(false);
                                                            }
                                                        }}
                                                    >
                                                        Confirm Deactivate
                                                    </Button>
                                                </div>
                                            </div>
                                        }
                                        title="Deactivate Account"
                                        trigger="click"
                                        open={deactivatePopoverVisible}
                                        onOpenChange={handleDeactivatePopoverOpen}
                                    >
                                        <Button
                                            size="small"
                                            type="primary"
                                            danger
                                            loading={deactivateLoading}
                                            style={{ marginLeft: 8 }}
                                            disabled={account.type === 'simulator_ttd'}
                                            title={` ${
                                                account.type === 'simulator_ttd'
                                                    ? 'You cannot deactivate Demo account'
                                                    : 'Deactivate funded account'
                                            }`}
                                        >
                                            Deactivate
                                        </Button>
                                    </Popover>
                                </>
                            ) : (
                                <>
                                    <div className="admin-account-status-pill admin-account-status-pill--inactive">
                                        <CloseCircleTwoTone
                                            twoToneColor="#bfbfbf"
                                            style={{ fontSize: 18 }}
                                        />
                                        <span>Inactive</span>
                                    </div>
                                    <Popover
                                        content={
                                            account.type === 'simulator_ttf' ? (
                                                <div style={{ minWidth: 200 }}>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Text>Expiration (days):</Text>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            value={expirationDays}
                                                            onChange={(e) =>
                                                                setExpirationDays(
                                                                    Number(e.target.value)
                                                                )
                                                            }
                                                            style={{ width: 80, marginLeft: 8 }}
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 8
                                                        }}
                                                    >
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                setActivatePopoverVisible(false)
                                                            }
                                                            disabled={activateLoading}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            loading={activateLoading}
                                                            onClick={async () => {
                                                                if (!account?.id) return;
                                                                setActivateLoading(true);
                                                                try {
                                                                    await activateFundedAccount(
                                                                        user?.token,
                                                                        {
                                                                            accountId: account.id,
                                                                            days: expirationDays
                                                                        }
                                                                    );
                                                                    message.success(
                                                                        `Account activated for ${expirationDays} days`
                                                                    );
                                                                    setActivatePopoverVisible(
                                                                        false
                                                                    );
                                                                    if (refetch) refetch();
                                                                } catch (err) {
                                                                    message.error(
                                                                        err?.response?.data
                                                                            ?.message ||
                                                                            'Failed to activate account'
                                                                    );
                                                                } finally {
                                                                    setActivateLoading(false);
                                                                }
                                                            }}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ minWidth: 280 }}>
                                                    <div style={{ marginBottom: 12 }}>
                                                        <Text>
                                                            Marking this account OUT will reactivate
                                                            it, clear the review state, and suppress
                                                            future automated drawdown retriggers.
                                                        </Text>
                                                    </div>
                                                    {isPTT && (
                                                        <Checkbox
                                                            checked={pttResetOnReinstate}
                                                            onChange={(e) =>
                                                                setPttResetOnReinstate(
                                                                    e.target.checked
                                                                )
                                                            }
                                                            style={{ marginBottom: 12 }}
                                                        >
                                                            Reset account data (paid reset)
                                                        </Checkbox>
                                                    )}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 8
                                                        }}
                                                    >
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                setActivatePopoverVisible(false)
                                                            }
                                                            disabled={activateLoading}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            loading={activateLoading}
                                                            onClick={async () => {
                                                                if (!account?.id) return;
                                                                setActivateLoading(true);
                                                                try {
                                                                    await activateFundedAccount(
                                                                        user?.token,
                                                                        {
                                                                            accountId: account.id,
                                                                            reinstateMode:
                                                                                isPTT &&
                                                                                pttResetOnReinstate
                                                                                    ? 'reset'
                                                                                    : 'mark_out'
                                                                        }
                                                                    );
                                                                    message.success(
                                                                        isPTT && pttResetOnReinstate
                                                                            ? 'TRPTT account reset and reactivated'
                                                                            : 'Account marked OUT and reactivated'
                                                                    );
                                                                    setActivatePopoverVisible(
                                                                        false
                                                                    );
                                                                    setPttResetOnReinstate(false);
                                                                    if (refetch) refetch();
                                                                } catch (err) {
                                                                    message.error(
                                                                        err?.response?.data
                                                                            ?.message ||
                                                                            'Failed to reactivate account'
                                                                    );
                                                                } finally {
                                                                    setActivateLoading(false);
                                                                }
                                                            }}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        title={
                                            account.type === 'simulator_ttf'
                                                ? 'Activate Account'
                                                : 'Mark OUT'
                                        }
                                        trigger="click"
                                        open={activatePopoverVisible}
                                        onOpenChange={handleActivatePopoverOpen}
                                    >
                                        <Button
                                            size="small"
                                            type="primary"
                                            loading={activateLoading}
                                        >
                                            {account.type === 'simulator_ttf'
                                                ? 'Activate'
                                                : 'Mark OUT'}
                                        </Button>
                                    </Popover>
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>

            <Row gutter={16} style={{ alignItems: 'stretch' }}>
                <Col span={10} style={{ display: 'flex' }}>
                    <Card
                        className="admin-account-card"
                        title={
                            <Text
                                className="admin-account-section-title"
                                style={{
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    width: '100%',
                                    display: 'block'
                                }}
                            >
                                Equity Curve
                            </Text>
                        }
                        style={{ width: '100%' }}
                    >
                        <div style={{ height: '50vh' }}>
                            {account.equityCurve &&
                            account.equityCurve.length > 0 &&
                            (account.equityCurve[0].length > 0 ||
                                account.equityCurve[1].length > 0) ? (
                                <RunningPnLLineChart data={account.equityCurve} />
                            ) : (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        color: '#888',
                                        paddingTop: '20vh'
                                    }}
                                >
                                    No data to plot
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                <Col span={14} style={{ display: 'flex' }}>
                    <Card
                        className="admin-account-card"
                        title={
                            <Text
                                className="admin-account-section-title"
                                style={{
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    width: '100%',
                                    display: 'block'
                                }}
                            >
                                Account Information
                            </Text>
                        }
                        style={{ width: '100%' }}
                    >
                        <Row gutter={[16, 16]} style={{ textAlign: 'left' }}>
                            {[
                                ['First Name', account.user?.first_name || '-'],
                                ['Last Name', account.user?.last_name || '-'],
                                ['Email Address', account.user?.email || '-'],
                                [
                                    'Account Start Date',
                                    account.accountStartDate
                                        ? dayjs(account.accountStartDate).format('MM/DD/YYYY')
                                        : '-'
                                ],
                                [
                                    'Last Trade Date',
                                    account.stats?.lastTradeDate
                                        ? dayjs(account.stats?.lastTradeDate).format('MM/DD/YYYY')
                                        : '-'
                                ],
                                [
                                    'Account End Date',
                                    account.accountEndDate
                                        ? toNyCalendarDate(account.accountEndDate).format(
                                              'MM/DD/YYYY'
                                          )
                                        : '-'
                                ],
                                ['Active Days', account.stats?.totalActiveDays ?? '-'],
                                [
                                    'Number of Resets',
                                    account.numberOfResets ? account.numberOfResets : 0
                                ],
                                ['Days Traded', account.stats?.totalDaysTradeTaken ?? '-']
                            ].map(([label, value]) => (
                                <Col span={8} key={label}>
                                    <div className="admin-account-data-point">
                                        <Text className="admin-account-label">{label}</Text>
                                        <div className="admin-account-value">{value}</div>
                                    </div>
                                </Col>
                            ))}

                            <Col span={24}>
                                <div
                                    className="admin-account-inline-panel"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <Text
                                        className="admin-account-label"
                                        style={{ marginBottom: 0 }}
                                    >
                                        Edit End Date
                                    </Text>
                                    <DatePicker
                                        value={endDateValue}
                                        onChange={(value) => setEndDateValue(value)}
                                        allowClear
                                    />
                                    <Button
                                        type="primary"
                                        size="small"
                                        loading={endDateSaving}
                                        onClick={handleSaveEndDate}
                                    >
                                        Save End Date
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setEndDateValue(null)}
                                        disabled={endDateSaving}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {showPasswordReset && (
                                <Col
                                    span={8}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        height: '100%'
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontWeight: 300,
                                            textAlign: 'left',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Reset Password:
                                    </Text>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            marginTop: 8
                                        }}
                                    >
                                        <Input.Password
                                            ref={passwordInputRef}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onFocus={() => setPasswordFocused(true)}
                                            onBlur={() => setPasswordFocused(false)}
                                            autoComplete="new-password"
                                        />
                                        <Button
                                            size="small"
                                            type="ghost"
                                            onClick={() => setPassword('')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="small"
                                            type="primary"
                                            disabled={!isPasswordValid}
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: 'Confirm Password Reset',
                                                    content:
                                                        'Are you sure you want to reset the password?',
                                                    onOk: async () => {
                                                        try {
                                                            await resetUserPassword(
                                                                user?.token,
                                                                account.user?.id,
                                                                password
                                                            );
                                                            message.success(
                                                                'Password reset successfully'
                                                            );
                                                            setPassword('');
                                                        } catch {
                                                            message.error(
                                                                'Failed to reset password'
                                                            );
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                    {(passwordFocused || password.length > 0) && (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {passwordRules.map(({ label, test }) => (
                                                <div
                                                    key={label}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <CheckOutlined
                                                        style={{
                                                            color: test(password)
                                                                ? 'green'
                                                                : 'lightgray',
                                                            marginRight: 4
                                                        }}
                                                    />{' '}
                                                    {label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Col>
                            )}

                            {showGenericEquityReset && (
                                <Col
                                    span={6}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        height: '100%'
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontWeight: 300,
                                            textAlign: 'left',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Reset Equity/Buying Power:
                                    </Text>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 16,
                                            marginTop: 12
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4
                                            }}
                                        >
                                            <span style={{ fontSize: 14, color: '#888' }}>
                                                Equity:&nbsp;<b>${account.equity ?? '-'}</b>
                                            </span>
                                            <span style={{ fontSize: 14, color: '#888' }}>
                                                Buying Power:&nbsp;
                                                <b>${account.availableFunds ?? '-'}</b>
                                            </span>
                                        </div>
                                        <Popover
                                            placement="bottom"
                                            content={
                                                <Form
                                                    layout="vertical"
                                                    style={{ minWidth: 220 }}
                                                    onFinish={async () => {
                                                        await Modal.confirm({
                                                            title: 'Confirm Equity Reset',
                                                            content: `Set initial funds to $${initialFunds} and leverage to ${leverage}?
                                                  ${
                                                      removeExistingTrades
                                                          ? 'This will remove all existing trades/orders/positions. '
                                                          : ''
                                                  }
                                                  ${
                                                      resetExpirationDate
                                                          ? `Expiration date will be reset to ${resetExpirationDays} days from now.`
                                                          : ''
                                                  }`,
                                                            onOk: async () => {
                                                                setResetEquityLoading(true);
                                                                try {
                                                                    // With this conditional payload building:
                                                                    const resetPayload = {
                                                                        id: account.id,
                                                                        initialFunds:
                                                                            Number(initialFunds),
                                                                        leverageRatio:
                                                                            Number(leverage),
                                                                        removeExistingTrades
                                                                    };
                                                                    // Only add expirationDays if resetExpirationDate is true
                                                                    if (resetExpirationDate) {
                                                                        resetPayload.expirationDays =
                                                                            Number(
                                                                                resetExpirationDays
                                                                            );
                                                                    }
                                                                    await resetAccountEquity(
                                                                        user?.token,
                                                                        resetPayload
                                                                    );
                                                                    message.success(
                                                                        'Equity reset successfully'
                                                                    );
                                                                    setPopoverVisible(false);
                                                                    setInitialFunds('');
                                                                    setLeverage('');
                                                                    setRemoveExistingTrades(true);
                                                                    setResetExpirationDate(true);
                                                                    setResetExpirationDays(90);
                                                                    passwordInputRef.current?.blur();
                                                                    if (refetch) refetch();
                                                                } catch (err) {
                                                                    message.error(
                                                                        'Failed to reset equity'
                                                                    );
                                                                } finally {
                                                                    setResetEquityLoading(false);
                                                                }
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <Form.Item
                                                        label="Initial Funds (New Equity)"
                                                        required
                                                    >
                                                        <Input
                                                            autoFocus
                                                            value={initialFunds}
                                                            onChange={(e) =>
                                                                setInitialFunds(
                                                                    e.target.value.replace(
                                                                        /[^\d.]/g,
                                                                        ''
                                                                    )
                                                                )
                                                            }
                                                            placeholder="e.g. 10000"
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Leverage Ratio (Multiplier)"
                                                        required
                                                    >
                                                        <Input
                                                            value={leverage}
                                                            onChange={(e) =>
                                                                setLeverage(
                                                                    e.target.value.replace(
                                                                        /[^\d.]/g,
                                                                        ''
                                                                    )
                                                                )
                                                            }
                                                            placeholder="e.g. 2"
                                                        />
                                                    </Form.Item>

                                                    <div style={{ marginBottom: 12 }}>
                                                        Buying Power:
                                                        <b>
                                                            {initialFunds && leverage
                                                                ? `${FORMAT.NUMBER_TO_CURRENCY(
                                                                      buyingPower
                                                                  )}`
                                                                : '-'}
                                                        </b>
                                                    </div>

                                                    {/* New checkbox options */}
                                                    <Form.Item>
                                                        <Checkbox
                                                            checked={removeExistingTrades}
                                                            onChange={(e) =>
                                                                setRemoveExistingTrades(
                                                                    e.target.checked
                                                                )
                                                            }
                                                        >
                                                            Remove all existing trades/positions
                                                        </Checkbox>
                                                    </Form.Item>

                                                    <Form.Item>
                                                        <Checkbox
                                                            checked={resetExpirationDate}
                                                            onChange={(e) =>
                                                                setResetExpirationDate(
                                                                    e.target.checked
                                                                )
                                                            }
                                                        >
                                                            Reset expiration date
                                                        </Checkbox>
                                                    </Form.Item>

                                                    {/* Conditional expiration days input */}
                                                    {resetExpirationDate && (
                                                        <Form.Item label="Expiration Days" required>
                                                            <Input
                                                                type="number"
                                                                value={resetExpirationDays}
                                                                onChange={(e) =>
                                                                    setResetExpirationDays(
                                                                        parseInt(
                                                                            e.target.value,
                                                                            10
                                                                        ) || 90
                                                                    )
                                                                }
                                                                placeholder="90"
                                                                min="1"
                                                            />
                                                        </Form.Item>
                                                    )}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 8
                                                        }}
                                                    >
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setPopoverVisible(false);
                                                                setInitialFunds('');
                                                                setLeverage('');
                                                                setRemoveExistingTrades(true);
                                                                setResetExpirationDate(true);
                                                                setResetExpirationDays(90);
                                                                passwordInputRef.current?.blur();
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            htmlType="submit"
                                                            loading={resetEquityLoading}
                                                            disabled={
                                                                !initialFunds ||
                                                                !leverage ||
                                                                Number(leverage) <= 0
                                                            }
                                                        >
                                                            Submit
                                                        </Button>
                                                    </div>
                                                </Form>
                                            }
                                            title="Reset Equity/Buying Power"
                                            trigger="click"
                                            open={popoverVisible}
                                            onOpenChange={handleEquityPopoverOpen}
                                            getPopupContainer={() =>
                                                popoverRef.current || document.body
                                            }
                                        >
                                            <Button
                                                size="small"
                                                type="primary"
                                                ref={popoverRef}
                                                onClick={() => handleEquityPopoverOpen(true)}
                                                loading={resetEquityLoading}
                                            >
                                                Reset
                                            </Button>
                                        </Popover>
                                    </div>
                                </Col>
                            )}

                            {isPTT && (
                                <Col
                                    span={6}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        height: '100%'
                                    }}
                                >
                                    <div className="admin-account-action-block">
                                        <Text className="admin-account-label">
                                            TRPTT Paid Reset
                                        </Text>
                                        <div
                                            style={{
                                                marginTop: 12,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 8
                                            }}
                                        >
                                            <Text className="admin-account-muted">
                                                Clears journal data, resets equity to baseline,
                                                clears violations, and reactivates the account.
                                            </Text>
                                            <Button
                                                type="primary"
                                                loading={activateLoading}
                                                onClick={() => {
                                                    Modal.confirm({
                                                        title: 'Run Paid Reset?',
                                                        content:
                                                            'This will delete TRPTT journal data, clear violations, reset equity to baseline, and reactivate the account.',
                                                        onOk: handlePttReset
                                                    });
                                                }}
                                            >
                                                Reset Account Data
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            )}

                            {showRiskControls && (
                                <Col span={10}>
                                    <Text
                                        style={{
                                            fontWeight: 300,
                                            textAlign: 'left',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        Apply Risk Controls:
                                    </Text>
                                    <Select
                                        mode="multiple"
                                        open={riskDropdownOpen}
                                        onDropdownVisibleChange={setRiskDropdownOpen}
                                        value={selectedRiskControls}
                                        onChange={handleRiskChange}
                                        placeholder="Select"
                                        style={{ width: '100%', marginTop: 8 }}
                                        dropdownRender={() => (
                                            <div>
                                                <div style={{ padding: 8, fontWeight: 600 }}>
                                                    Risk Controls
                                                </div>
                                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                    {RISK_CONTROLS.map((opt) => (
                                                        <div
                                                            key={opt.value}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '4px 12px'
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={selectedRiskControls.includes(
                                                                    opt.value
                                                                )}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedRiskControls(
                                                                            (prev) =>
                                                                                prev.includes(
                                                                                    opt.value
                                                                                )
                                                                                    ? prev
                                                                                    : [
                                                                                          ...prev,
                                                                                          opt.value
                                                                                      ]
                                                                        );
                                                                    } else {
                                                                        setSelectedRiskControls(
                                                                            (prev) =>
                                                                                prev.filter(
                                                                                    (v) =>
                                                                                        v !==
                                                                                        opt.value
                                                                                )
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {opt.label}
                                                            </Checkbox>
                                                            {opt.hasInput &&
                                                                selectedRiskControls.includes(
                                                                    opt.value
                                                                ) &&
                                                                (opt.value === 'unwind_all' ? (
                                                                    <DatePicker
                                                                        showTime
                                                                        allowClear
                                                                        style={{
                                                                            marginLeft: 8,
                                                                            width: 180
                                                                        }}
                                                                        placeholder="Select date/time"
                                                                        value={
                                                                            riskControlValues.unwind_all
                                                                                ? dayjs(
                                                                                      riskControlValues.unwind_all
                                                                                  )
                                                                                : null
                                                                        }
                                                                        onChange={(date) =>
                                                                            handleRiskDateChange(
                                                                                'unwind_all',
                                                                                date
                                                                            )
                                                                        }
                                                                        onClick={(e) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Input
                                                                        type="number"
                                                                        size="small"
                                                                        style={{
                                                                            marginLeft: 8,
                                                                            width: 100
                                                                        }}
                                                                        placeholder="Value"
                                                                        value={
                                                                            riskControlValues[
                                                                                opt.value
                                                                            ]
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleRiskInputChange(
                                                                                opt.value,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        onClick={(e) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    />
                                                                ))}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        padding: 8
                                                    }}
                                                >
                                                    <Button size="small" onClick={handleRiskReset}>
                                                        Reset
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        onClick={handleRiskSave}
                                                        loading={riskSaveLoading}
                                                        disabled={riskSaveLoading}
                                                    >
                                                        Save Controls
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        dropdownStyle={{ minWidth: 260 }}
                                        showSearch={false}
                                        tagRender={() => null}
                                        // renderValue is broken in AntD v4/v5 for mode=multiple, so use 'displayRender' hack
                                        // Instead, render a custom display below the Select
                                    />
                                    <div style={{ minHeight: 24, marginTop: 4 }}>
                                        {Object.keys(riskConfigOverrides).length === 0 ? (
                                            <span style={{ color: '#bfbfbf' }}>
                                                No overrides applied, using global risk values
                                            </span>
                                        ) : (
                                            <span style={{ color: '#52c41a' }}>
                                                Overrides applied to this account
                                            </span>
                                        )}
                                    </div>
                                    {isRiskConfigLoading && (
                                        <div style={{ marginTop: 8 }}>
                                            <Spin size="small" /> Loading risk controls...
                                        </div>
                                    )}
                                </Col>
                            )}

                            <Col span={24}>
                                <Divider />
                                <Text className="admin-account-label">Note</Text>
                                <Input.TextArea
                                    value={note}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        if (value.length <= 1000) {
                                            setNote(value);
                                        }
                                    }}
                                    placeholder="Enter note (max 1000 characters)"
                                    rows={4}
                                    maxLength={1000}
                                    style={{ marginTop: 8 }}
                                    showCount
                                />
                                <div
                                    style={{
                                        marginTop: 10,
                                        display: 'flex',
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={handleSaveNote}
                                        loading={noteSaveLoading}
                                        disabled={!note.trim() || noteSaveLoading}
                                    >
                                        Save Note
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 24, textAlign: 'left' }}>
                <Col span={16}>
                    <Card
                        title={
                            <Text
                                style={{
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    width: '100%',
                                    display: 'block'
                                }}
                            >
                                Account Statistics
                            </Text>
                        }
                    >
                        <Row gutter={[16, 16]}>
                            {[
                                [
                                    'Total Profit and Loss',
                                    (() => {
                                        const totalPnL = account.stats?.totalPnL ?? 0;
                                        let color;
                                        if (totalPnL < 0) {
                                            color = 'red';
                                        } else if (totalPnL > 0) {
                                            color = 'green';
                                        } else {
                                            color = 'black';
                                        }
                                        return (
                                            <span key="total-pnl" style={{ color }}>
                                                ${totalPnL}
                                            </span>
                                        );
                                    })()
                                ],
                                [
                                    'Largest Win',
                                    (() => {
                                        const largestWin = account.stats?.largestWin;
                                        const totalPnL = account.stats?.totalPnL;
                                        let isRed = false;
                                        if (totalPnL && largestWin) {
                                            isRed = largestWin >= 0.3 * totalPnL;
                                        }
                                        let spanColor;
                                        if (isRed) {
                                            spanColor = 'red';
                                        } else if (largestWin != null) {
                                            spanColor = 'green';
                                        } else {
                                            spanColor = 'black';
                                        }
                                        return (
                                            <span
                                                key="largest-win"
                                                style={{
                                                    color: spanColor
                                                }}
                                            >
                                                ${largestWin}
                                                {isRed && (
                                                    <span
                                                        style={{
                                                            color: '#bfbfbf',
                                                            fontSize: 12,
                                                            marginLeft: 6
                                                        }}
                                                    >
                                                        (&gt;30% Total P&amp;L)
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    })()
                                ],
                                ['Total Adjusted P/L', `$${account.stats?.totalAdjustedPnL}`],
                                ['Total Commissions', `$${account.stats?.totalCommissions}`],
                                ['Total Tickets (Executions)', account.stats?.totalTickets],
                                ['Total Trades', account.stats?.totalTrades],
                                [
                                    '10-Day Rolling Average for Trades',
                                    (() => {
                                        const avg = account.stats?.rolling10DayAvg;
                                        const isGreen = avg >= 2;
                                        const isRed = avg < 2;
                                        return (
                                            <span
                                                key="rolling-trades"
                                                style={{ color: isGreen ? 'green' : 'red' }}
                                            >
                                                {avg}
                                                {isRed && (
                                                    <span
                                                        style={{
                                                            color: '#bfbfbf',
                                                            fontSize: 12,
                                                            marginLeft: 6
                                                        }}
                                                    >
                                                        (&lt; 2 trades)
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    })()
                                ],
                                [
                                    'Equity Loss Limit Violations',
                                    account.stats?.equityLossLimitViolations
                                ],
                                [
                                    'Traded Stocks Below $5',
                                    (() => {
                                        const stocks = account.stats?.stocksBelow5 || [];
                                        if (!stocks.length) {
                                            return <span key="stocks-below-5">No</span>;
                                        }
                                        const tickers = stocks.map((row) => row.symbol).join(', ');
                                        return (
                                            <span key="stocks-below-5" style={{ color: 'red' }}>
                                                Yes ({tickers})
                                            </span>
                                        );
                                    })()
                                ]
                            ].map(([label, value]) => (
                                <Col span={6} key={label}>
                                    <Text
                                        style={{
                                            fontWeight: 500,
                                            textAlign: 'left',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {label}
                                    </Text>
                                    <br />
                                    {value}
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card
                        title={
                            <Text
                                style={{
                                    fontWeight: 500,
                                    textAlign: 'left',
                                    width: '100%',
                                    display: 'block',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Account Reports
                            </Text>
                        }
                    >
                        <div>
                            <Text>Trade Execution Report: </Text>
                            <Button
                                size="small"
                                type="link"
                                loading={tradeReportLoading}
                                onClick={handleReportDownload}
                                style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                                disabled={tradeReportLoading}
                            >
                                Download
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>
            <Modal
                title={modalTitle}
                open={eligibilityModalOpen}
                onOk={handleEligibilityModalOk}
                onCancel={handleEligibilityModalCancel}
                okText="Confirm"
                cancelText="Cancel"
                okButtonProps={{
                    disabled: !eligible && (!selectedReasons || selectedReasons.length === 0)
                }}
            >
                <div style={{ marginBottom: 12 }}>
                    {eligible
                        ? 'This will clear risk violation for this account.'
                        : 'This will mark this account with a risk violation.'}
                </div>
                {!eligible && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Reasons <span style={{ color: '#d4380d' }}>*</span>:
                        </div>
                        <Checkbox.Group
                            options={REASON_OPTIONS}
                            value={selectedReasons}
                            onChange={(values) => {
                                setSelectedReasons(values);
                            }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
}
